import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, KeyRound, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email, code, success
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      toast.success("Si el email existe, recibirás un código");
      setStep("code");
    } catch (err) {
      setError(err.response?.data?.detail || "Error al enviar el código");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    if (newPassword.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        email,
        code,
        new_password: newPassword
      });
      toast.success("Contraseña actualizada");
      setStep("success");
    } catch (err) {
      setError(err.response?.data?.detail || "Error al restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center">
          <Link to="/" className="flex justify-center mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_proplandpro/artifacts/d99lgtpt_inmopages-fondoblanco.png"
              alt="InmoPages"
              className="h-12"
            />
          </Link>
          <CardTitle className="text-2xl">
            {step === "success" ? "Listo" : "Recuperar Contraseña"}
          </CardTitle>
          <CardDescription>
            {step === "email" && "Ingresa tu email y te enviaremos un código"}
            {step === "code" && `Ingresa el código enviado a ${email}`}
            {step === "success" && "Tu contraseña ha sido actualizada"}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="forgot-error">
              {error}
            </div>
          )}

          {/* Step 1: Email */}
          {step === "email" && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="pl-10"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-brand-purple hover:bg-brand-pink"
                disabled={loading}
                data-testid="forgot-send-btn"
              >
                {loading ? "Enviando..." : "Enviar Código"}
              </Button>
            </form>
          )}

          {/* Step 2: Code + New Password */}
          {step === "code" && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="code">Código de verificación</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456"
                    className="pl-10 text-center text-lg tracking-widest"
                    maxLength={6}
                    required
                    data-testid="forgot-code-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="newPassword">Nueva contraseña</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  data-testid="forgot-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite tu contraseña"
                  required
                  data-testid="forgot-confirm-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-brand-purple hover:bg-brand-pink"
                disabled={loading}
                data-testid="forgot-reset-btn"
              >
                {loading ? "Actualizando..." : "Restablecer Contraseña"}
              </Button>
              <button
                type="button"
                onClick={() => { setStep("email"); setError(""); }}
                className="w-full text-sm text-gray-500 hover:text-brand-purple"
              >
                ¿No recibiste el código? Reenviar
              </button>
            </form>
          )}

          {/* Step 3: Success */}
          {step === "success" && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-gray-600">Ya puedes iniciar sesión con tu nueva contraseña.</p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full bg-brand-purple hover:bg-brand-pink"
                data-testid="forgot-login-btn"
              >
                Ir a Iniciar Sesión
              </Button>
            </div>
          )}

          {step !== "success" && (
            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-gray-500 hover:text-brand-purple flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Volver al login
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
